import {
	HttpClient,
	HttpResponse
}                             from '@angular/common/http';
import { Injectable }         from '@angular/core';
import { AbstractApiService } from '@app/core/http/abstract-api.service';
import { SanitizerService }   from '@app/core/http/sanitizer.service';
import { TransitionService }  from '@app/core/http/transition.service';
import {
	IErsHttpParams,
	RequestActionEnum,
	RequestCompleteEntityState,
	RequestCompleteListState,
	RequestState
}                             from '@app/core/interfaces/api.interface';
import { ModelTypeEnum }      from '@app/core/models/transition.model';
import {
	ErsExternal,
	ErsExternalStatusEnum
}                             from '@app/modules/ers/models/ers.model';
import { LoggerService }      from '@app/shared/services/logger.service';
import { MapperService }      from '@http/mapper.service';
import { IHttpContextConfig } from '@interceptors/interceptors.model';
import {
	ErsExternalScopesEnum,
	FeatureScopesEnum
}                             from '@models/scope.enums';
import {
	CreateExtErsRequestParams,
	DeleteExtErsRequestParams,
	ErsDeviceTypeEnumDTO,
	ExternalErsApiClient,
	ExtErsBodyDTO,
	ExtErsDTO,
	ExtErsStatusEnumDTO,
	GetAllExtErsRequestParams,
	GetExtErsByBusinessPremisesRequestParams,
	GetExtErsByExtTssRequestParams,
	GetExtErsRequestParams,
	InvalidEntitiesDTO,
	UpdateExtErsRequestParams
}                             from '@openApiGenerated/management-api';
import {
	Observable,
	of
}                             from 'rxjs';
import { delay }              from 'rxjs/operators';

@Injectable( {
	providedIn: 'root'
} )
export class ErsExternalApiService extends AbstractApiService {

	constructor(
		private readonly _externalErsApiClient: ExternalErsApiClient,
		private readonly _mapperService: MapperService,
		private readonly _sanitize: SanitizerService,
		private readonly _transition: TransitionService,
		protected http: HttpClient,
		protected logger: LoggerService
	) {
		super();
		this.http = http;
	}

	private sanitizeApiResponse( entity: ErsExternal, context = this ): void {
		if ( entity instanceof ErsExternal ) {
			entity.acquisitionDate = context._sanitize.date<Date>( entity.acquisitionDate, ModelTypeEnum.TMO, Date );
			entity.commissioningDate = context._sanitize.date<Date>( entity.commissioningDate, ModelTypeEnum.TMO, Date );
			entity.createdDate = context._sanitize.date<Date>( entity.createdDate, ModelTypeEnum.TMO, Date );
			entity.deactivationDate = context._sanitize.date<Date>( entity.deactivationDate, ModelTypeEnum.TMO, Date );
			entity.deviceType = context._mapperService.ers.deviceTypeDtoToTmo( entity?.deviceType as unknown as ErsDeviceTypeEnumDTO );
			entity.lastModifiedDate = context._sanitize.date<Date>( entity.lastModifiedDate, ModelTypeEnum.TMO, Date );
			entity.tssConnectionDate = context._sanitize.date<Date>( entity.tssConnectionDate, ModelTypeEnum.TMO, Date );
			entity.tssEntityId = entity.extTssEntityId;

			if ( entity.errors?.invalidEntities ) {
				entity.errors.invalidEntities =
					context._mapperService.errors.invalidEntitiesDtoToTmo( entity.errors.invalidEntities as unknown as InvalidEntitiesDTO[] );
			}
		}
	}


	// LIST TYPE REQUESTS #############################
	public list(
		options?: {
			httpContextConfig: IHttpContextConfig,
			throwError?: boolean
		}
	): Observable<RequestCompleteListState<ErsExternal>> {
		const {
			httpContextConfig,
			throwError = false
		} = options;

		this.logger.debug( 'pre-list call' );

		const params: IErsHttpParams = {
			sort: 'lastModifiedDate,desc'
		};

		return this.search( { httpContextConfig, params, throwError } );
	}

	public search(
		options: {
			enablePermissionCheck?: boolean,
			httpContextConfig?: IHttpContextConfig,
			params: IErsHttpParams,
			throwError?: boolean
		}
	): Observable<RequestCompleteListState<ErsExternal>> {
		const {
			enablePermissionCheck = true,
			httpContextConfig,
			params,
			throwError = false
		} = options;

		this.logger.debug( 'pre-search call: ', params );

		if ( enablePermissionCheck && !this.hasAllPermissions( [ FeatureScopesEnum.ERIC, ErsExternalScopesEnum.List ] ) ) {
			return of(
				this._transition.getEmptyRequestCompleteListState<ErsExternal>( RequestActionEnum.LIST, RequestState.FAILED_REQUEST_PRE_VALIDATION )
			).pipe( delay( 100 ) );
		}

		const requestParam: GetAllExtErsRequestParams = {
			ersIds: this.arrayUtils.checkForSingleValueArgAndReturnAsArray<string>( params?.ersIds ),
			ersSerialNumber: params?.ersSerialNumber,
			filter: params?.filter,
			hasValidationResults: params?.hasValidationResults,
			pageNumber: params?.paging?.pageNumber,
			pageSize: params?.paging?.pageSize,
			search: this.arrayUtils.checkForSingleValueArgAndReturnAsArray<string>( params?.search ),
			sort: params?.sort,
			status: this._mapperService.ers.statusListExternalTmoToDto(
				this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.status as unknown as ErsExternalStatusEnum )
			),
		};

		const req$ = this._externalErsApiClient.getAllExtErs(
			requestParam,
			'body',
			false,
			this.buildHttpContext( httpContextConfig )
		);

		return this._transition.handleListRequestResponse<ExtErsDTO, ErsExternal>(
			req$,
			ErsExternal,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			throwError
		);
	}

	public listByBusinessPremisesId(
		options: {
			businessPremisesId: string
			enablePermissionCheck?: boolean,
			httpContextConfig?: IHttpContextConfig,
			params: IErsHttpParams,
			throwError?: boolean
		}
	): Observable<RequestCompleteListState<ErsExternal>> {
		const {
			businessPremisesId,
			enablePermissionCheck = true,
			httpContextConfig,
			params,
			throwError = false
		} = options;

		this.logger.debug( 'pre-listByBusinessPremisesId call: ', businessPremisesId );

		if ( enablePermissionCheck && !this.hasAllPermissions( [ FeatureScopesEnum.ERIC, ErsExternalScopesEnum.List ] ) ) {
			return of(
				this._transition.getEmptyRequestCompleteListState<ErsExternal>( RequestActionEnum.LIST, RequestState.FAILED_REQUEST_PRE_VALIDATION )
			).pipe( delay( 100 ) );
		}

		const requestParam: GetExtErsByBusinessPremisesRequestParams = {
			businessPremisesId,
			ersSerialNumber: params?.ersSerialNumber,
			filter: params?.filter,
			hasValidationResults: params?.hasValidationResults,
			pageNumber: params?.paging?.pageNumber,
			pageSize: params?.paging?.pageSize,
			search: this.arrayUtils.checkForSingleValueArgAndReturnAsArray<string>( params?.search ),
			sort: params?.sort,
			status: this.arrayUtils.checkForSingleValueArgAndReturnAsArray<ExtErsStatusEnumDTO>(
				params?.status as unknown as ExtErsStatusEnumDTO[]
			),
		};

		const req$ = this._externalErsApiClient.getExtErsByBusinessPremises(
			requestParam,
			'body',
			false,
			this.buildHttpContext( httpContextConfig )
		);

		return this._transition.handleListRequestResponse<ExtErsDTO, ErsExternal>(
			req$,
			ErsExternal,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			throwError
		);
	}

	public listByTssId(
		options: {
			httpContextConfig?: IHttpContextConfig,
			params?: IErsHttpParams,
			throwError?: boolean,
			tssId: string,
		}
	): Observable<RequestCompleteListState<ErsExternal>> {
		const {
			httpContextConfig,
			params,
			throwError = false,
			tssId
		} = options;

		this.logger.debug( 'pre-listByTssId call: ', tssId );

		const requestParam: GetExtErsByExtTssRequestParams = {
			ersSerialNumber: params?.ersSerialNumber,
			filter: params?.ersSerialNumber,
			hasValidationResults: params?.hasValidationResults,
			pageNumber: params?.paging?.pageNumber,
			pageSize: params?.paging?.pageSize,
			search: this.arrayUtils.checkForSingleValueArgAndReturnAsArray<string>( params?.search ),
			sort: params?.sort,
			status: this._mapperService.ers.statusListExternalTmoToDto(
				this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.status as unknown as ErsExternalStatusEnum )
			),
			tssId
		};

		const req$ = this._externalErsApiClient.getExtErsByExtTss(
			requestParam,
			'body',
			false,
			this.buildHttpContext( httpContextConfig )
		);

		return this._transition.handleListRequestResponse<ExtErsDTO, ErsExternal>(
			req$,
			ErsExternal,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			throwError
		);
	}

	// ENTITY TYPE REQUESTS ###########################
	public read(
		options: {
			ersId: string,
			httpContextConfig?: IHttpContextConfig,
			throwError?: boolean,
			withFullResponse?: boolean
		}
	): Observable<RequestCompleteEntityState<ErsExternal>> {
		const {
			ersId,
			httpContextConfig,
			throwError = false,
			withFullResponse = false
		} = options;


		this.logger.debug( 'pre-read call: ', `id: ${ ersId }`, `withFullResponse: ${ withFullResponse }` );
		const requestParam: GetExtErsRequestParams = {
			ersId
		};

		const req$ = this._externalErsApiClient.getExtErs(
			requestParam,
			withFullResponse ? 'response' : undefined,
			false,
			this.buildHttpContext( httpContextConfig )
		);

		return this._transition.handleEntityRequestResponse<ExtErsDTO | HttpResponse<ExtErsDTO>, ErsExternal>(
			req$,
			RequestActionEnum.READ,
			ErsExternal,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public create(
		options: {
			ers: ErsExternal,
			httpContextConfig?: IHttpContextConfig,
			throwError?: boolean
		}
	): Observable<RequestCompleteEntityState<ErsExternal>> {
		const {
			ers,
			httpContextConfig,
			throwError = false
		} = options;

		this.logger.debug( 'pre-create call: ', { ...ers } );

		const extErsBodyDTO: ExtErsBodyDTO = this._mapperService.ers.ersExternalToExtErsBodyDTO( ers, this._sanitize );
		const requestParam: CreateExtErsRequestParams = {
			businessPremisesId: ers.businessPremisesId,
			extErsBodyDTO
		};

		const req$ = this._externalErsApiClient.createExtErs(
			requestParam,
			'body',
			false,
			this.buildHttpContext( httpContextConfig )
		);

		return this._transition.handleEntityRequestResponse<ExtErsDTO, ErsExternal>(
			req$,
			RequestActionEnum.CREATE,
			ErsExternal,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public update(
		options: {
			ers: ErsExternal,
			httpContextConfig?: IHttpContextConfig,
			throwError?: boolean,
			withFullResponse?: boolean
		}
	): Observable<RequestCompleteEntityState<ErsExternal>> {
		const {
			ers,
			httpContextConfig,
			throwError = false,
			withFullResponse = false
		} = options;

		this.logger.debug( 'pre-update call: ', { ...ers } );

		const extErsBodyDTO: ExtErsBodyDTO = this._mapperService.ers.ersExternalToExtErsBodyDTO( ers, this._sanitize );
		const requestParam: UpdateExtErsRequestParams = {
			ersId: ers.id,
			ifMatch: ers.etag,
			extErsBodyDTO
		};

		const req$ = this._externalErsApiClient.updateExtErs(
			requestParam,
			withFullResponse ? 'response' : undefined,
			false,
			this.buildHttpContext( httpContextConfig )
		);

		return this._transition.handleEntityRequestResponse<ExtErsDTO | HttpResponse<ExtErsDTO>, ErsExternal>(
			req$,
			RequestActionEnum.UPDATE,
			ErsExternal,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public delete(
		options: {
			ersId: string,
			httpContextConfig?: IHttpContextConfig,
			throwError?: boolean
		}
	): Observable<RequestCompleteEntityState<ErsExternal>> {
		const {
			ersId,
			httpContextConfig,
			throwError = false
		} = options;

		this.logger.debug( 'pre-delete call: ', ersId );
		const requestParam: DeleteExtErsRequestParams = {
			ersId
		};

		const req$ = this._externalErsApiClient.deleteExtErs(
			requestParam,
			'response',
			false,
			this.buildHttpContext( httpContextConfig )
		);

		return this._transition.handleEntityRequestResponse<HttpResponse<any>, ErsExternal>(
			req$,
			RequestActionEnum.DELETE,
			ErsExternal,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}
}
